import 'isomorphic-fetch';

import { ClientConfig } from 'client/configuration';
import { IS_NODE, ENVIRONMENT_URL } from 'client/utils/environment';
import { withRetry } from 'client/utils/promise';
import { isClientError, HTTP_NO_CONTENT } from 'client/utils/http-status';
import { logger } from 'client/utils/logger/logger';
import { apiCache } from 'client/utils/cache-provider';
import { featureFlags } from 'client/utils/feature-flags';

const DEFAULT_TIMEOUT_MS = 500;
const DEFAULT_RETRIES = 2;
const ABSOLUTE_URL_PATTERN = /^https?:\/\//i;

/**
 * @typedef {Object} AdditionalMetadata
 * @property {Object} json - JSON response from {Promise}
 * @property {Boolean} loadedFromLocalCache - indicating, that data coming from local cache
 */

/**
 * @typedef {Object} FetchOptions
 * @property {boolean} [includeResponseInError=false] - If true, when a non-ok HTTP status is received, the promise will reject with an error containing the response text.
 * @property {boolean} [showAPIError=false] - If true, when a non-ok HTTP status is received, the promise will attempt to parse the response as JSON and reject with an error containing the `message` property from the JSON response.
 * @property {global.fetch} [fetch=global.fetch] - ***MUST BE USED IN EXCEPTIONAL CASES***. Possibility to override the used fetch for individual requests or the whole APIClient. For example, if you want to override the `Host` header.
 * @property {number} [timeout] - Request timeout in milliseconds. Defaults to {@link DEFAULT_TIMEOUT_MS}.
 * @property {number} [retries] - Number of retries for the request. Defaults to {@link DEFAULT_RETRIES}.
 * @property {boolean} [useVenomCache] - Cache control options. true to always check and potentially use cache. `false` to bypass cache.
 * @property {boolean} [returnAdditionalMetadata=false] - If true, the {@link fetchJson} method will return an object {@link AdditionalMetadata}.
 * @property {HeadersInit} [headers] - Custom headers to include in the request.
 */

function addErrorMetadata(error, request, response) {
  if (error) {
    // extract page name and action name from 'x-client-action-name' if available
    if (request.options.headers) {
      const actionName = request.options.headers['x-client-action-name'] || 'unknown';
      error.actionName = actionName; // eslint-disable-line no-param-reassign
      error.pageName = actionName.split('.')[0]; // eslint-disable-line no-param-reassign
    }
    error.apiUrl = response ? response.url : request.url; // eslint-disable-line no-param-reassign

    // TimeoutError name coming from native fetch
    // AbortError name coming from node-fetch and used only for signal error
    // The error name "AbortError" is thrown by node-fetch when an AbortSignal is triggered.
    // This can happen due to a timeout or a manual abort. However, there is no way to
    // distinguish between a manual abort and a timeout-triggered AbortSignal.
    // Since we are not manually aborting requests and node-fetch is not heavily used here,
    // we can safely assume the error is due to a timeout.
    if (error.name === 'TimeoutError' || error.name === 'AbortError') {
      error.status = 408; // eslint-disable-line no-param-reassign
    }
  }

  return error;
}

function wrapResponse(request, response) {
  const originalJson = response.json.bind(response); // Store original json method.

  response.json = () => {
    if (response.status === HTTP_NO_CONTENT) {
      return Promise.resolve({}); // added because `response.json()` will throw an error for 204s
    }
    return originalJson().catch(error => {
      throw addErrorMetadata(error, request, response);
    });
  };

  return response;
}

function wrapError(response, errorMessage, responseText, errorCustom) {
  const error = new Error(errorMessage);
  error.status = response.status;
  error.responseText = responseText;
  if (isClientError(error.status)) {
    error.suspendRetries = true;
  }
  error.custom = errorCustom;
  return error;
}

function getOptionsForFetch(fetchOptions, config) {
  const { timeout, useVenomCache, ...options } = fetchOptions;

  if (config.useDeadline) {
    const { headers } = options;

    options.headers = {
      ...headers,
      'x-deadline': Date.now() + timeout,
    };
  }

  if (IS_NODE && timeout) {
    options.signal = AbortSignal.timeout(timeout);
  }

  return options;
}

/**
 * Returns default error message about failed request.
 *
 * @param url request url.
 * @param status response status.
 * @returns {string} error message.
 */
function getErrorMessage(url, status) {
  return `Request to ${url} failed, got status ${status}`;
}

function fetchWithStatus(
  url,
  { showAPIError = false, includeResponseInError = false, fetch: optionsFetch, ...options },
  config = {}
) {
  const request = {
    url,
    options,
  };

  // Possibility to override the used fetch for individual requests or the whole APIClient
  const fetch = optionsFetch ?? config.fetch ?? global.fetch;

  return fetch(url, getOptionsForFetch(options, config))
    .then(response => {
      if (response.ok) {
        return wrapResponse(request, response);
      }

      if (showAPIError) {
        // The Promise returned from fetch() won’t reject on HTTP error status even if the response is an HTTP 404 or 500.
        // Instead, it will resolve normally (with ok status set to false), and it will only reject on network failure
        // or if anything prevented the request from completing.
        // So we need this workaround to get origin API error when request is failed.
        return response
          .json()
          .then(jsonResponse => Promise.reject(wrapError(response, jsonResponse.message, null, jsonResponse)));
      }

      if (includeResponseInError) {
        return response
          .text()
          .then(text => Promise.reject(wrapError(response, getErrorMessage(url, response.status), text)));
      }

      return Promise.reject(wrapError(response, getErrorMessage(url, response.status)));
    })
    .catch(error => {
      throw addErrorMetadata(error, request);
    });
}

function doFetch(url, options, config) {
  const { retries } = options;
  if (IS_NODE) {
    logger.debug(`[  API  ] fetch ${url}\n`);
  }

  return withRetry(fetchWithStatus, retries)(url, options, config);
}

/**
 * here is a way to bypass local cache based on a feature flag
 * 1. have your model call fetchJson('/bla/', { useVenomCache: featureFlags.cacheBla })
 * 2. at any point turning off the cacheBla feature flag will ensure your page will bypass the local cache for that endpoint
 * @param {FetchOptions} options passed to fetchJson()
 * @returns {false|*|boolean} true if cache is enabled both globally and for specific api endpoint
 */
function isCacheEnabled(options) {
  const { venomCache, venomCacheBrowser } = featureFlags.get();
  const { useVenomCache } = options;
  if (venomCache && useVenomCache) {
    if (IS_NODE) {
      return true;
    }
    if (venomCacheBrowser) {
      return true;
    }
  }
  return false;
}

export function json(res) {
  return res.json();
}

export class APIClient {
  constructor(baseUrl, config) {
    this.baseUrl = baseUrl;
    this.config = config;
  }

  /**
   * The ClientConfig will only be populated after the first call to ClientConfig.load(),
   * which is currently called in server.js and index[-dev].jsx.
   *
   * @see /server/server.js
   * @see /client/index.jsx
   * @see /client/index-dev.jsx
   * @see /client/configuration.js
   */
  static getDefaultOptions() {
    return {
      timeout: ClientConfig.get('apiDefaultTimeoutMs', DEFAULT_TIMEOUT_MS),
      retries: DEFAULT_RETRIES,
    };
  }

  /**
   * Returns true if API timeout is disabled, false by default
   * Provides developers an option to disable API timeout in local environment in case of slow connection
   *
   * @returns {Boolean}
   */
  static isApiTimeoutDisabled() {
    return ClientConfig.get('apiTimeoutDisabled', false);
  }

  eventSource(url, options) {
    const baseUrl = typeof this.baseUrl === 'function' ? this.baseUrl(url) : this.baseUrl;
    return new EventSource(`${baseUrl}${url}`, options);
  }

  /**
   * Uses fetch API to connect to an API resource.
   *
   * @param {string} resourcePath - relative path to the API resource
   * @param {FetchOptions} options - request options
   *
   * @returns {Promise}
   */
  fetch(resourcePath, options) {
    // handle lazy load
    const url = typeof this.baseUrl === 'function' ? this.baseUrl(resourcePath) : this.baseUrl;

    const fetchOptions = {
      ...APIClient.getDefaultOptions(),
      ...options,
    };

    if (APIClient.isApiTimeoutDisabled()) {
      // disables timeout in node-fetch
      fetchOptions.timeout = 0;
    }

    return doFetch(`${url}${resourcePath}`, fetchOptions, this.config);
  }

  /**
   * Wrapper function around fetch that simply returns the json response.
   *
   * @param {string} resourcePath - relative path to the API resource
   * @param {FetchOptions} options - request options
   *
   * @returns {Object | AdditionalMetadata | Promise<Object | AdditionalMetadata>} JSON response from {Promise} or {ReturnObject} in case if returnAdditionalMetadata provided
   */
  fetchJson(resourcePath, options = {}) {
    const cacheEnabled = isCacheEnabled(options);
    const returnAdditionalMetadata = !!options?.returnAdditionalMetadata;
    const metadata = { loadedFromLocalCache: false };

    // checking cache regardless of the cache flag in fetch options
    if (cacheEnabled) {
      const response = apiCache.get(resourcePath);
      if (typeof response !== 'undefined') {
        logger.debug(
          `\x1b[0m[ \x1b[35mCACHE\x1b[0m ]\x1b[33m retrieved ${resourcePath.slice(0, 200)} from cache\x1b[0m`
        );

        metadata.loadedFromLocalCache = true;
        return Promise.resolve(returnAdditionalMetadata ? { json: response, ...metadata } : response);
      }
    }

    return this.fetch(resourcePath, options).then(async response => {
      const responseJson = response.json();
      const data = await responseJson;

      // saving response to the cache depending on useVenomCache value
      if (cacheEnabled) {
        const matches = response.headers?.get?.('cache-control')?.match(/max-age=(\d+)/);
        const maxAge = matches ? parseInt(matches[1], 10) : 0;
        if (maxAge > 0) apiCache.set(resourcePath, data, maxAge * 1000);
      }

      return returnAdditionalMetadata ? { json: data, ...metadata } : data;
    });
  }
}

/**
 * @example
 * const config = {
 *   default: '/gateway/api',
 *   vehicle: 'https://localhost:8080/api',
 * };
 * getMatchingBaseUrl(config, '/inventory/v2/') // => '/gateway/api'
 * getMatchingBaseUrl(config, '/vehicle/v2/') // => 'localhost:8080/api'
 */
function getBaseUrl(baseUrls, resourcePath) {
  const apiName = resourcePath.substring(1, resourcePath.indexOf('/', 1));
  return apiName in baseUrls ? baseUrls[apiName] : baseUrls.default;
}

function getFinalApiUrl(clientConfigUrl) {
  return IS_NODE && !ABSOLUTE_URL_PATTERN.test(clientConfigUrl)
    ? `${ENVIRONMENT_URL}${clientConfigUrl}`
    : clientConfigUrl;
}

/**
 * Returns all base URL strings used by APIClient instances in this file.
 * Used to derive CSP connect-src origins for MCP iframe sandboxing.
 *
 * When adding a new APIClient, add its config URL(s) here.
 */
export function getApiBaseUrls() {
  return [
    ...Object.values(ClientConfig.get('edmundsApiUrl') || {}),
    ClientConfig.get('paymentCalculatorApiUrl'),
    ClientConfig.get('carBuying')?.apiUrl,
    ClientConfig.get('udmApiUrl'),
    ClientConfig.get('carCodeApiUrl'),
    ClientConfig.get('intelligentMessagingApiUrl'),
    ClientConfig.get('idmApiUrl'),
    ClientConfig.get('idmProfilesApiUrl'),
    ClientConfig.get('idmVanillaForumApiUrl'),
    ClientConfig.get('popularSearchesApiUrl'),
    ClientConfig.get('widgetStoreApiUrl'),
    ClientConfig.get('evaApiUrl'),
    ClientConfig.get('venomApiUrl'),
    ClientConfig.get('mobileRestApiUrl'),
    ClientConfig.get('edmundsPartnerOfferApi'),
    ClientConfig.get('edmundsPartnerOfferGatewayApi'),
    ClientConfig.get('edmundsMultiOfferApi'),
    ClientConfig.get('edmundsMultiOfferGatewayApi'),
    ClientConfig.get('edmundsIcoApi'),
    ClientConfig.get('privatePartyApi')?.apiUrl,
    ClientConfig.get('gearGarageApiUrl'),
  ].filter(Boolean);
}

/**
 * Returns all base URL strings used by APIClient instances in this file.
 * Used to derive CSP connect-src origins for MCP iframe sandboxing.
 *
 * Specifically, MCP iframe uses these URLs to construct the origins for images, css, and js bundles
 */
// TODO: Use getStaticUrl() instead of accessing ClientConfig for 'unversionedUrl' once https://edmunds.atlassian.net/browse/PLAT-4636 is completed
export function getResourceBaseUrls() {
  return [`https:${ClientConfig.get('unversionedUrl')}`];
}

export const EdmundsAPI = new APIClient(
  resourcePath => {
    const apiUrl = getBaseUrl(ClientConfig.get('edmundsApiUrl'), resourcePath);

    return getFinalApiUrl(apiUrl);
  },
  { useDeadline: true }
);

export const PaymentsCalculatorAPI = new APIClient(() => ClientConfig.get('paymentCalculatorApiUrl'));

export const CarBuyingAPI = new APIClient(() => ClientConfig.get('carBuying').apiUrl);

export const UdmAPI = new APIClient(() => ClientConfig.get('udmApiUrl'));

export const CarCodeApi = new APIClient(() => ClientConfig.get('carCodeApiUrl'));

export const IntelligentMessagingAPI = new APIClient(() => ClientConfig.get('intelligentMessagingApiUrl'));

export const idmAPI = new APIClient(() => ClientConfig.get('idmApiUrl'));

export const idmProfilesAPI = new APIClient(() => ClientConfig.get('idmProfilesApiUrl'));

export const idmVanillaForumAPI = new APIClient(() => ClientConfig.get('idmVanillaForumApiUrl'));

export const PopularSearchesAPI = new APIClient(() => ClientConfig.get('popularSearchesApiUrl'));

export const WidgetStoreApi = new APIClient(() => ClientConfig.get('widgetStoreApiUrl'));

export const EvaApi = new APIClient(() => ClientConfig.get('evaApiUrl'));

export const VenomApi = new APIClient(() => ClientConfig.get('venomApiUrl'));

export const MobileRestApiClient = new APIClient(() => ClientConfig.get('mobileRestApiUrl'));

export const CaramelApi = url => new APIClient(() => url);

export const SecurityCredentialsAPI = new APIClient(
  () => ClientConfig.get('awsSecurityCredentialsUrl') + process.env.AWS_CONTAINER_CREDENTIALS_RELATIVE_URI
);

export const EdmundsPartnerOfferAPI = new APIClient(() => getFinalApiUrl(ClientConfig.get('edmundsPartnerOfferApi')));

export const EdmundsPartnerOfferGatewayAPI = new APIClient(() =>
  getFinalApiUrl(ClientConfig.get('edmundsPartnerOfferGatewayApi'))
);

export const EdmundsMultiOfferAPI = new APIClient(() => getFinalApiUrl(ClientConfig.get('edmundsMultiOfferApi')));

export const EdmundsMultiOfferGatewayAPI = new APIClient(() =>
  getFinalApiUrl(ClientConfig.get('edmundsMultiOfferGatewayApi'))
);

export const EdmundsIcoAPI = new APIClient(() => getFinalApiUrl(ClientConfig.get('edmundsIcoApi')));

export const externalDataAPI = url => new APIClient(() => url);

export const PrivatePartyAPI = new APIClient(() => ClientConfig.get('privatePartyApi').apiUrl);

export const GearGarageAPI = new APIClient(() => ClientConfig.get('gearGarageApiUrl'));
